import React, { useState } from 'react';
import { Checkbox, Box, Typography, MenuItem, Select, ListItemText } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTheme } from '@mui/material/styles';
import {
  ASSET_CLASS_GROUPS,
  UNDERLYING_CLASS_CATEGORIES,
  ALL_IDS_WITH_PARENTS,
} from '../../configs/exchangeVolume';

/**
 * AssetClassFilter - Two-level grouped multi-select for asset class / underlying class.
 * Follows the Category.jsx pattern: expandable parents with checkboxes, indeterminate state.
 *
 * @param {number[]} selectedItems - Currently selected IDs (parents + children)
 * @param {function} handleChange - Called with synthetic { target: { value } } event
 * @param {number} width - Optional width (default 280)
 */
const AssetClassFilter = ({ selectedItems, handleChange, width = 280 }) => {
  const theme = useTheme();

  const [expanded, setExpanded] = useState({});

  const handleExpandToggle = (label) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    setExpanded((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  return (
    <Box size="small" sx={{ height: '30px', width, display: 'flex', flexDirection: 'row' }}>
      <Select
        size="small"
        sx={{
          backgroundColor: theme.tableHeader.highlight,
          width,
          height: '30px',
          transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
            },
          },
          '&.Mui-focused': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
              borderWidth: '2px',
            },
          },
        }}
        multiple
        value={selectedItems}
        onChange={handleChange}
        renderValue={(selected) => {
          if (selected.length === 0) {
            return (
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '12px', pt: '3px' }}>
                Filter by Asset Class
              </Typography>
            );
          }

          // Check if all are selected
          if (selected.length >= ALL_IDS_WITH_PARENTS.length) {
            return (
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '12px', pt: '3px' }}>
                All Asset Classes
              </Typography>
            );
          }

          // Build labels
          const selectedLabels = ASSET_CLASS_GROUPS.flatMap((parent) => {
            const children = UNDERLYING_CLASS_CATEGORIES.filter((c) => c.parentId === parent.id);
            const selectedChildren = children.filter((c) => selected.includes(c.id));

            if (selectedChildren.length === children.length) {
              return parent.label;
            }
            return selectedChildren.map((c) => c.label);
          }).filter(Boolean);

          let displayText = selectedLabels.join(', ');
          const CHAR_LIMIT = 38;
          if (displayText.length > CHAR_LIMIT) {
            displayText = displayText.substring(0, CHAR_LIMIT) + '...';
          }

          return (
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '12px', pt: '3px' }}>
              {displayText}
            </Typography>
          );
        }}
        MenuProps={{
          PaperProps: {
            elevation: 4,
            sx: {
              width: 'auto',
              backgroundColor: theme.background.transparent,
              backdropFilter: 'blur(8px)',
              borderRadius: '8px',
              mt: 0.5,
              maxHeight: 400,
              '&::-webkit-scrollbar': { width: '8px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': {
                background: theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.2)'
                  : 'rgba(0, 0, 0, 0.2)',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.3)'
                  : 'rgba(0, 0, 0, 0.3)',
              },
            },
          },
        }}
      >
        {ASSET_CLASS_GROUPS.flatMap((parent, parentIndex) => {
          const isExpanded = expanded[parent.label] || false;
          const isParentSelected = selectedItems.includes(parent.id);
          const children = UNDERLYING_CLASS_CATEGORIES.filter((c) => c.parentId === parent.id);
          const someChildrenSelected = children.some((c) => selectedItems.includes(c.id));
          const allChildrenSelected = children.every((c) => selectedItems.includes(c.id));

          const header = (
            <Box
              key={`${parent.id}-header`}
              role="button"
              aria-expanded={isExpanded}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  setExpanded((prev) => ({ ...prev, [parent.label]: !prev[parent.label] }));
                }
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                height: '32px',
                px: 1.5,
                cursor: 'pointer',
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.03)'
                  : 'rgba(0, 0, 0, 0.02)',
                borderTop: parentIndex > 0
                  ? `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`
                  : 'none',
                transition: 'background-color 0.1s ease',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.06)'
                    : 'rgba(0, 0, 0, 0.04)',
                },
              }}
              onClick={handleExpandToggle(parent.label)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <ChevronRightIcon
                  sx={{
                    fontSize: 16,
                    color: theme.palette.text.secondary,
                    transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                    transition: 'transform 0.15s ease',
                  }}
                />
                {parent.icon && (
                  <Box sx={{ display: 'flex', alignItems: 'center', color: theme.palette.text.secondary, fontSize: 14 }}>
                    {parent.icon}
                  </Box>
                )}
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontWeight: 600,
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.3px',
                  }}
                >
                  {parent.label}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Checkbox
                  indeterminate={someChildrenSelected && !allChildrenSelected}
                  size="small"
                  checked={isParentSelected || allChildrenSelected}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleChange({ target: { value: isParentSelected
                      ? selectedItems.filter((id) => id !== parent.id)
                      : [...selectedItems, parent.id]
                    }});
                  }}
                  sx={{
                    p: 0,
                    '& .MuiSvgIcon-root': { fontSize: 16 },
                  }}
                />
              </Box>
            </Box>
          );

          const subMenuItems = children.map((child) => {
            const isChildSelected = selectedItems.includes(child.id);

            if (!isExpanded) {
              return (
                <MenuItem
                  key={child.id}
                  value={child.id}
                  sx={{ display: 'none' }}
                />
              );
            }

            return (
              <MenuItem
                key={child.id}
                value={child.id}
                sx={{
                  minHeight: '26px',
                  height: '26px',
                  py: 0.25,
                  pl: 3.5,
                  pr: 1.5,
                  justifyContent: 'space-between',
                  backgroundColor: isChildSelected
                    ? theme.palette.mode === 'dark'
                      ? 'rgba(145, 166, 205, 0.16)'
                      : 'rgba(63, 100, 168, 0.16)'
                    : 'transparent',
                  borderLeft: isChildSelected
                    ? `2px solid ${theme.palette.primary.main}`
                    : '2px solid transparent',
                  cursor: 'pointer',
                  transition: 'background-color 0.1s ease',
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                {child.icon && (
                  <Box sx={{ display: 'flex', alignItems: 'center', color: theme.palette.text.secondary, minWidth: 18, mr: 0.5 }}>
                    {child.icon}
                  </Box>
                )}
                <ListItemText
                  primary={
                    <Typography
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: '12px',
                        lineHeight: 1.2,
                      }}
                      variant="body2"
                    >
                      {child.label}
                    </Typography>
                  }
                  sx={{ my: 0 }}
                />
                <Checkbox
                  size="small"
                  checked={isChildSelected}
                  sx={{
                    p: 0,
                    ml: 0.5,
                    '& .MuiSvgIcon-root': { fontSize: 16 },
                  }}
                />
              </MenuItem>
            );
          });

          return [header, ...subMenuItems];
        })}
      </Select>
    </Box>
  );
};

export default AssetClassFilter;
